import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PaymentTransaction {
  id: string;
  conversation_id: string;
  client_id: string;
  provider_id: string;
  amount_cents: number;
  currency: string;
  platform_fee_cents: number;
  provider_amount_cents: number;
  status: string;
  transfer_status?: string;
  created_at: string;
  completed_at?: string;
}

interface PaymentDispute {
  id: string;
  transaction_id: string;
  stripe_dispute_id: string;
  amount_cents: number;
  currency: string;
  reason: string;
  status: string;
  evidence?: string;
  evidence_due_by?: string;
  created_at: string;
}

interface PaymentRefund {
  id: string;
  transaction_id: string;
  stripe_refund_id: string;
  amount_cents: number;
  currency: string;
  reason?: string;
  status: string;
  created_at: string;
}

export const usePaymentTransactions = () => {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [disputes, setDisputes] = useState<PaymentDispute[]>([]);
  const [refunds, setRefunds] = useState<PaymentRefund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchTransactions = async () => {
    if (!user) {
      setTransactions([]);
      setDisputes([]);
      setRefunds([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user's payment transactions - only select needed columns
      // Sensitive Stripe IDs (payment_intent_id, stripe_account_id, stripe_transfer_id) 
      // are excluded for security. Admins can access full data via StripeTestSuite.

      // Fetch transactions for the user
      const { data: transactionData, error: transactionError } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          conversation_id,
          client_id,
          provider_id,
          amount_cents,
          currency,
          platform_fee_cents,
          provider_amount_cents,
          status,
          transfer_status,
          created_at,
          completed_at
        `)
        .or(`client_id.eq.${user.id},provider_id.eq.${user.id}`)
        .neq('status', 'draft') // Exclude draft transactions - not yet paid
        .order('created_at', { ascending: false });

      if (transactionError) {
        throw new Error(transactionError.message);
      }

      // All transactions returned are now either pending (payment in progress), 
      // completed, disputed, or refunded - drafts are filtered at query level
      setTransactions(transactionData || []);

      // Fetch disputes and refunds for user's transactions
      if (transactionData && transactionData.length > 0) {
        const transactionIds = transactionData.map(t => t.id);
        
        // Fetch disputes
        const { data: disputeData, error: disputeError } = await supabase
          .from('payment_disputes')
          .select('*')
          .in('transaction_id', transactionIds)
          .order('created_at', { ascending: false });

        if (disputeError) {
          console.error('Error fetching disputes:', disputeError);
        } else {
          setDisputes(disputeData || []);
        }
        
        // Fetch refunds
        const { data: refundData, error: refundError } = await supabase
          .from('payment_refunds')
          .select('*')
          .in('transaction_id', transactionIds)
          .order('created_at', { ascending: false });

        if (refundError) {
          console.error('Error fetching refunds:', refundError);
        } else {
          setRefunds(refundData || []);
        }
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching payment transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [user]);

  // Calculate financial summaries
  const getFinancialSummary = () => {
    if (!user) return null;

    // All paid transactions as client (completed, pending, refunded - excludes draft/failed)
    const paidAsClient = transactions.filter(t => 
      t.client_id === user.id && ['completed', 'pending', 'refunded'].includes(t.status)
    );
    // All paid transactions as provider
    const paidAsProvider = transactions.filter(t => 
      t.provider_id === user.id && ['completed', 'pending', 'refunded'].includes(t.status)
    );

    // Pending transactions - payment in progress (webhook received but transfer may be pending)
    const pendingAsClient = transactions.filter(t => 
      t.client_id === user.id && t.status === 'pending'
    );
    const pendingAsProvider = transactions.filter(t => 
      t.provider_id === user.id && t.status === 'pending'
    );
    
    // Calculate total refunds received by client (money returned)
    const clientTransactionIds = new Set(paidAsClient.map(t => t.id));
    const refundsReceivedCents = refunds
      .filter(r => r.status === 'succeeded' && clientTransactionIds.has(r.transaction_id))
      .reduce((sum, r) => sum + r.amount_cents, 0);
    
    const totalRefunded = refundsReceivedCents / 100;

    // Calculate earnings from paid transactions as provider
    const totalEarnings = paidAsProvider.reduce((sum, t) => sum + t.provider_amount_cents, 0) / 100;
    const pendingEarnings = pendingAsProvider.reduce((sum, t) => sum + t.provider_amount_cents, 0) / 100;
    
    // Calculate gross spending (all payments made as client)
    const grossSpent = paidAsClient.reduce((sum, t) => sum + t.amount_cents, 0) / 100;
    const pendingSpent = pendingAsClient.reduce((sum, t) => sum + t.amount_cents, 0) / 100;

    // Net spent = gross spent minus refunds received
    const netSpent = grossSpent - totalRefunded;

    return {
      totalSpent: netSpent,
      pendingSpent: pendingSpent,
      totalEarned: totalEarnings,
      pendingEarnings: pendingEarnings,
      totalPlatformFees: paidAsClient.reduce((sum, t) => sum + t.platform_fee_cents, 0) / 100,
      totalTransactions: paidAsProvider.length + paidAsClient.length,
      pendingTransactions: pendingAsProvider.length + pendingAsClient.length,
      disputedTransactions: disputes.length,
      activeDisputes: disputes.filter(d => !['won', 'lost', 'charge_refunded'].includes(d.status)).length,
      totalRefunds: refunds.length,
      totalRefunded
    };
  };

  const getTransactionsByConversation = (conversationId: string) => {
    return transactions.filter(t => t.conversation_id === conversationId);
  };

  const getDisputesForTransaction = (transactionId: string) => {
    return disputes.filter(d => d.transaction_id === transactionId);
  };

  const getRefundsForTransaction = (transactionId: string) => {
    return refunds.filter(r => r.transaction_id === transactionId);
  };

  return {
    transactions,
    disputes,
    refunds,
    loading,
    error,
    refetch: fetchTransactions,
    getFinancialSummary,
    getTransactionsByConversation,
    getDisputesForTransaction,
    getRefundsForTransaction
  };
};